import React, { useState } from 'react';
import { Input, Form } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SessionForm from '../../../../components/forms/SessionForm';
import AntdCustomButton from '../../../../components/buttons/AntdCustomButton';
import { H1, BodyText } from '../../../../components/ui/Typography';
import ScreenNavigationModel, {
  ScreenType
} from '../../models/ScreenNavigationModel';
import BusinessModel from '../../models/BusinessModel';
import { observer } from 'mobx-react-lite';
import HelpBoxCompanionModel from '../../../../models/HelpBoxCompanionModel';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../../components/animations/StepAnimations';

const NotFoundCompany: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const initialValues = {
    companyName: BusinessModel.companyName || '',
    zipOrCity: BusinessModel.zipOrCity || ''
  };
  const [isVisible, setIsVisible] = useState(true);
  const [nextScreen, setNextScreen] = useState<ScreenType | null>(null);
  const [loading, setLoading] = useState(false);

  const handleScreenChange = () => {
    setIsVisible(false);
  };

  const handleSearch = async (values: any) => {
    setLoading(true);
    BusinessModel.businessLoading = true;
    BusinessModel.setCompanyName(values.companyName || '');
    BusinessModel.setZipOrCity(values.zipOrCity || '');
    let searchPath = values.companyName;
    if (values.zipOrCity && values.zipOrCity.trim() !== '') {
      searchPath += ' zip:' + values.zipOrCity;
    }
    try {
      
      await BusinessModel.findBusinessOnGoogle(searchPath);

      if (BusinessModel.businessList.length === 0) {

        await BusinessModel.findBusinessOnGMB(searchPath);
        
        if (BusinessModel.businessList.length > 0) {
          setNextScreen(ScreenType.SELECT_YOUR_COMPANY);
          handleScreenChange();
        }
      } else {
        setNextScreen(ScreenType.SELECT_YOUR_COMPANY);
        handleScreenChange();
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
      BusinessModel.businessLoading = false;
    }
  };

  const handleNoProfile = () => {
    setNextScreen(ScreenType.ENTER_YOUR_COMPANY_DATA);
    handleScreenChange();
  };

  const helpBoxTitleMessage = t('Why were no companies found?');
  const helpBoxParagraphMessage = t(
    "We are looking for Google for company names or industry at the specified location. If no hits appear, more general information-maybe the name or category is different . Without Google entry you can also enter the data manually."
  );

  React.useEffect(() => {
    HelpBoxCompanionModel.setDisplayHelpBox(true);
    HelpBoxCompanionModel.setTitleMessage(helpBoxTitleMessage);
    HelpBoxCompanionModel.setParagraphMessage(helpBoxParagraphMessage);
  }, [i18n.language]);

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (nextScreen) {
          setTimeout(() => {
            ScreenNavigationModel.setCurrentScreen(nextScreen);
          }, 200);
        }
      }}>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainerVariants}
          exit="hidden"
          className="items-start sm:items-center sm:text-center justify-start sm:justify-center flex-1">
          <div>
            <motion.div variants={fadeInUpVariants} className="w-full mb-[40px]">
              <H1>{t('No company found')} 🙁</H1>
              <BodyText className="text-center">
                {t(
                  'Try again by looking just for the industry in your zip code or place'
                )}
              </BodyText>
            </motion.div>

            <SessionForm
              form={form}
              formName="businessSearchForm"
              formProps={{
                layout: 'vertical',
                onFinish: handleSearch,
                initialValues
              }}>
              <motion.div variants={fadeInUpVariants} className="mb-[36px]">
                <div className="w-full flex flex-col md:flex-row gap-4 mb-4 md:items-end justify-center">
                  <Form.Item
                    name="companyName"
                    label={
                      <span className="font-semibold text-base">
                        {t('Company name')}
                      </span>
                    }
                    className="flex-1 mb-0">
                    <Input
                      placeholder={t('Company name or industry of your company')}
                      autoFocus
                    />
                  </Form.Item>
                  <Form.Item
                    name="zipOrCity"
                    label={
                      <span className="font-semibold text-base">
                        {t('Postal code or city')}
                      </span>
                    }
                    className="flex-1 mb-0">
                    <Input
                      placeholder={t('Postal code or city')}
                    />
                  </Form.Item>
                  <Form.Item 
                    className="mb-0"
                    shouldUpdate>
                    {({ getFieldValue }) => {
                      const companyName = getFieldValue('companyName');
                      const zipOrCity = getFieldValue('zipOrCity');
                      const isButtonDisabled = !companyName?.trim() && !zipOrCity?.trim();

                      return (
                        <AntdCustomButton
                          appearance="important"
                          htmlType="submit"
                          icon={<RightOutlined />}
                          disabled={isButtonDisabled || loading}
                          loading={loading}
                          style={{ padding: '10px 20px' }}
                        />
                      );
                    }}
                  </Form.Item>
                </div>
              </motion.div>
            </SessionForm>

            <motion.div variants={fadeInUpVariants}>
              <AntdCustomButton
                appearance="default"
                onClick={handleNoProfile}>
                {t("I don't have a company profile on Google")}
              </AntdCustomButton>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default NotFoundCompany;